import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery, pyrotchBaseUrl } from "../axios/AxiosInstance";

export type CalendarEventPriority = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
export type CalendarEventType = "PROJECT" | "MILESTONE";

export interface CalendarEventUser {
    userId: number;
    userProfilePhotos?: string;
    userNames?: string;
}

export interface CalendarEvent {
    projectId: number;
    name: string;
    startDate?: string;
    deadlineDate?: string;
    completionDate?: string;
    sentToApprovalDate?: string;
    approvalDate?: string;
    key: string;
    status: string;
    type: CalendarEventType;
    priority?: CalendarEventPriority;
    dependsOnMilestoneId?: number;
    progressPercentage: number;
    userData: CalendarEventUser[];
}

export interface CalendarCapacityEntry {
    userId: number;
    userName: string;
    date: string;
    itemCount: number;
}

export const CalendarApi = createApi({
    baseQuery: axiosBaseQuery({
        baseUrl: pyrotchBaseUrl + "v1/",
    }),
    reducerPath: "CalendarApi",
    tagTypes: ['Calendar'],
    endpoints: (build) => ({
        getCalendarData: build.query<any, { projectId: number }>({
            query: ({ projectId }) => ({
                url: `project/${projectId}/calendar`,
                method: "GET",
            }),
        }),

        getAdmincalendarData: build.query<CalendarEvent[], { startDate: string, endDate: string, userId: number, projectId: number}>({
            query: ({startDate, endDate, userId, projectId}) => ({
                url: 'calendar/events/filter',
                method: "GET",
                params: {startDate, endDate, userId, projectId}
            }),
            providesTags: ['Calendar'],
        }),

        getCalendarCapacity: build.query<CalendarCapacityEntry[], { startDate: string, endDate: string, userId?: number }>({
            query: ({ startDate, endDate, userId = 0 }) => ({
                url: 'calendar/events/capacity',
                method: "GET",
                params: { startDate, endDate, userId },
            }),
        }),

        updateMilestoneSchedule: build.mutation<CalendarEvent, { milestoneId: number, startDate: string, endDate: string }>({
            query: ({ milestoneId, startDate, endDate }) => ({
                url: `calendar/events/${milestoneId}/schedule`,
                method: "PATCH",
                data: { startDate, endDate },
            }),
            invalidatesTags: ['Calendar'],
        }),

        updateMilestonePriority: build.mutation<CalendarEvent, { milestoneId: number, priority?: CalendarEventPriority, dependsOnMilestoneId?: number | null }>({
            query: ({ milestoneId, priority, dependsOnMilestoneId }) => ({
                url: `calendar/events/${milestoneId}/priority`,
                method: "PATCH",
                data: { priority, dependsOnMilestoneId },
            }),
            invalidatesTags: ['Calendar'],
        }),
    }),
});

export const {
    useLazyGetCalendarDataQuery,
    useGetCalendarDataQuery,
    useLazyGetAdmincalendarDataQuery,
    useLazyGetCalendarCapacityQuery,
    useUpdateMilestoneScheduleMutation,
    useUpdateMilestonePriorityMutation,
} = CalendarApi



